import React, {useContext, useEffect, useRef, useState} from 'react';
import {DebounceInput} from 'react-debounce-input';
import SvgIcon from '../../Components/Icons/SvgIcon';
import ArrowDown from '../../Components/Icons/ArrowDown';
import {ISearchFilter, ISearchFilterItem} from '../../Foundations/Types/ISearchFilter';
import Dropdown from '../../Components/Dropdown/Dropdown';
import searchFilterContext from '../../Foundations/Contexts/SearchFilterContext';

interface IProps {
    filter: ISearchFilter;
    onSelected(filter: ISearchFilter): void;
}

const renderSearchItem = (
    item: ISearchFilterItem,
    index: number,
    handleSelection: (item: ISearchFilterItem) => (e: React.SyntheticEvent<HTMLLabelElement>) => void) => (
    <div key={index} className={`${item.disabled === true ? 'cursor-not-allowed pointer-events-none' : ''}`}>
        <label onClick={handleSelection(item)}
               className="block p-2 rounded hover:bg-blue-200 cursor-pointer flex w-full"
        >
            <span className="w-4">
                 <input checked={item.default === true || item.selected === true}
                        readOnly
                        disabled={item.disabled}
                        className="pointer-events-none"
                        type="checkbox"
                 />
            </span>
            <span className={`inline-block ml-1 text-gray-600 pointer-events-none w-5/6 break-words ${item.disabled === true && item.default === false ? 'opacity-50' : ''}`}>
                {item.label}
           </span>
        </label>
    </div>
)

const SearchFilter: React.FC<IProps> = ({filter, onSelected }) => {
    const {activeFilters} = useContext(searchFilterContext)
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const toggleButtonRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        if (activeFilters === null) {
            setSearchQuery('');
        }
    }, [activeFilters]);

    const getButtonClass = (): string => {
        return filter.hasSelection === true
            ? 'bg-blue-200 text-blue'
            : 'bg-gray-300 text-gray-700';
    }

    const handleSelection = (item: ISearchFilterItem) => (e: React.SyntheticEvent<HTMLLabelElement>) => {
        e.preventDefault();

        if (typeof filter.items === 'undefined' || item.disabled === true) {
            return;
        }

        let hasSelection: boolean = false;

        const selectedItems: ISearchFilterItem[] = filter.items
            .map((source) => {
                if (source.value === item.value) {
                    source.selected = !source.selected;
                }

                if (filter.disableMultiSelect === true && source.value !== item.value) {
                    source.selected = false;
                }

                if (source.selected === true) {
                    hasSelection = source.selected;
                }

                return source;
            });

        if (hasSelection === false && filter.isDynamic === true) {
            selectedItems.map((item) => {
                item.hidden = false;
                return item;
            })
        }

        onSelected({
            ...filter,
            items: selectedItems,
            hasSelection,
            // flag if activeFilter should be updated
            updateLinkedFilter: typeof filter.linkedFilter !== 'undefined'
        });
    }

    const handleClickOutside = () => {
        setIsDropdownOpen(false);
    }

    const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchQuery(e.target.value);
    }

    const filterItemBySearchQuery = (item: ISearchFilterItem): boolean => {
        if (item.selected === true || item.hidden === true) {
            return false;
        }

        const vehicleName: string = item.value.toString().toLowerCase().replace('or similar', '');

        // use exact match
       return vehicleName.indexOf(searchQuery.toLowerCase()) !== -1;
    }

    const renderFilteredItems = (items: ISearchFilterItem[]) => {
       const filteredItems: ISearchFilterItem[] = items.filter((item) => {
           if (searchQuery === '' || searchQuery.length < 3) {
               return item.selected !== true && item.hidden === false;
           }
           return filterItemBySearchQuery(item);
       });

       if (searchQuery !== '' && filteredItems.length === 0) {
           return (
               <div className="text-gray-600 p-2 text-center">
                   No results found for "<span className="font-bold">{searchQuery}</span>"
               </div>
           )
       }

       return filteredItems.map((item: ISearchFilterItem, index) => (
           renderSearchItem(item, index, handleSelection)
       ))
    }

    return (
        <div className="relative mr-1 mb-3 inline-block">
            <button onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    ref={toggleButtonRef}
                    className={`${getButtonClass()} flex items-center font-bold px-2 py-1 mr-2 rounded focus:outline-none`}>
                { filter.label }
                <SvgIcon className={`inline-block transform ${isDropdownOpen ? 'rotate-180' : ''} ${filter.hasSelection === true ? 'fill-blue' : 'fill-gray-700'}`}>
                    <ArrowDown />
                </SvgIcon>
            </button>
            {
                isDropdownOpen === true &&
                <Dropdown className="mt-1 py-3 px-2 z-30 min-w-200"
                          doubleWidth
                          triggerRef={toggleButtonRef}
                          onClickOutside={() => handleClickOutside()}>
                    {
                        filter.isSearchAble !== true &&
                        typeof filter.items !== 'undefined' &&
                        filter.items.map((item: ISearchFilterItem, index: number) =>
                            renderSearchItem(item, index, handleSelection)
                        )
                    }
                    {
                        filter.isSearchAble === true &&
                        typeof filter.items !== 'undefined' &&
                        <div className="p-3">
                            <DebounceInput
                                onChange={handleQueryChange}
                                className="focus:outline-none border border-gray-400 block w-full py-1 px-2"
                                value={searchQuery}
                                placeholder="enter model name"
                            />
                        </div>
                    }
                    {
                        filter.isSearchAble === true &&
                        typeof filter.items !== 'undefined' &&
                        filter.items
                            .filter((item) => item.selected === true)
                            .map((item, index) => (
                                renderSearchItem(item, index, handleSelection)
                            ))
                    }
                    {
                        filter.isSearchAble === true &&
                        typeof filter.items !== 'undefined' &&
                        (
                            <div className="overscroll-y-contain overflow-y-auto max-h-300 border-gray border-t">
                                {
                                   renderFilteredItems(filter.items)
                                }
                            </div>
                        )
                    }
                </Dropdown>
            }
        </div>
    )
}

export default SearchFilter;
